/**
 * Slippy-map tile math (spec 04) — Web Mercator EPSG:3857, the scheme Leaflet
 * and Kartverket's webmercator matrix share. Pure; used for offline region
 * enumeration and download estimates.
 */

export interface Bbox {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface TileCoord {
  z: number;
  x: number;
  y: number;
}

/** Average tile size for download estimates (spec 04: ~15 KB). */
export const AVG_TILE_BYTES = 15 * 1024;

export function lonToTileX(lon: number, z: number): number {
  return Math.floor(((lon + 180) / 360) * 2 ** z);
}

export function latToTileY(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z,
  );
}

/** Web Mercator (EPSG:3857) half-extent in metres. */
const HALF_WORLD_M = Math.PI * 6378137; // 20037508.3427892

/**
 * Bounding box of a slippy tile in EPSG:3857 metres — for building WMS GetMap
 * requests from z/x/y (spec: depth-data overlay).
 */
export function tileBbox3857(
  z: number,
  x: number,
  y: number,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const span = (2 * HALF_WORLD_M) / 2 ** z;
  const minX = -HALF_WORLD_M + x * span;
  const maxY = HALF_WORLD_M - y * span; // slippy y grows southward
  return { minX, minY: maxY - span, maxX: minX + span, maxY };
}

/** Inverse: tile-space x (fractional) → longitude. */
export function tileXToLon(x: number, z: number): number {
  return (x / 2 ** z) * 360 - 180;
}

/** Inverse: tile-space y (fractional) → latitude. */
export function tileYToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

function clampTile(v: number, z: number): number {
  const max = 2 ** z - 1;
  return Math.min(max, Math.max(0, v));
}

/** Count tiles a bbox covers across [zMin, zMax] without materializing them. */
export function countTilesForBbox(bbox: Bbox, zMin: number, zMax: number): number {
  let total = 0;
  for (let z = zMin; z <= zMax; z++) {
    const x0 = clampTile(lonToTileX(bbox.west, z), z);
    const x1 = clampTile(lonToTileX(bbox.east, z), z);
    // Note: y grows southward, so north maps to the smaller y.
    const y0 = clampTile(latToTileY(bbox.north, z), z);
    const y1 = clampTile(latToTileY(bbox.south, z), z);
    total += (Math.abs(x1 - x0) + 1) * (Math.abs(y1 - y0) + 1);
  }
  return total;
}

/** Enumerate every tile a bbox covers across [zMin, zMax]. */
export function tilesForBbox(bbox: Bbox, zMin: number, zMax: number): TileCoord[] {
  const tiles: TileCoord[] = [];
  for (let z = zMin; z <= zMax; z++) {
    const xs = [clampTile(lonToTileX(bbox.west, z), z), clampTile(lonToTileX(bbox.east, z), z)];
    const ys = [clampTile(latToTileY(bbox.north, z), z), clampTile(latToTileY(bbox.south, z), z)];
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    const yMin = Math.min(...ys);
    const yMax = Math.max(...ys);
    for (let x = xMin; x <= xMax; x++) {
      for (let y = yMin; y <= yMax; y++) {
        tiles.push({ z, x, y });
      }
    }
  }
  return tiles;
}

export interface DownloadEstimate {
  tileCount: number;
  bytes: number;
}

export function estimateDownload(bbox: Bbox, zMin: number, zMax: number): DownloadEstimate {
  const tileCount = countTilesForBbox(bbox, zMin, zMax);
  return { tileCount, bytes: tileCount * AVG_TILE_BYTES };
}
